import { AdminService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import type { QueryFunctionContext } from '@tanstack/react-query';
import type { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import type { TCall } from 'app/queries/types/TCall';
import { pickOrganizationFeatureFlags, TOrganizationFeatureFlags } from '../helpers/pickOrganizationFeatureFlags';

type TParams = QueryFunctionContext<ReturnType<typeof organizationsKeys.featureFlagsByOrg>>;

const SLUG_MATCH_PAGE_SIZE = 50;

export const createFetchOrganizationFeatureFlagsQueryFn =
  (call: TCall) =>
  async ({ queryKey: [{ organizationId, slug }], signal }: TParams): Promise<TOrganizationFeatureFlags> => {
    let page = 1;
    for (;;) {
      const response = await call(() =>
        cancelOnSignal(
          AdminService.getOrganizationsList({
            pageNo: String(page),
            pageSize: String(SLUG_MATCH_PAGE_SIZE),
            orderBy: 'createdAt',
            orderType: 'desc',
            search: slug,
            includeExternallyManaged: true,
          }),
          signal,
        ),
      );
      const flags = pickOrganizationFeatureFlags(response, organizationId);
      if (flags) {
        return flags;
      }
      const isLastPage = !response.data?.length || page * SLUG_MATCH_PAGE_SIZE >= (response.count ?? 0);
      if (isLastPage) {
        throw new Error(`Organization ${organizationId} was not found in the admin organizations list`);
      }
      page += 1;
    }
  };
